import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import AuthModal from "../../subscribe-trial/components/AuthModal";

const SLIDES = [
  {
    id: 1,
    image:
      "https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20elegant%20lifestyle%20photography%20of%20high-end%20fashion%20products%20arranged%20artfully%20on%20a%20light%20marble%20surface%20with%20soft%20natural%20lighting%20and%20subtle%20shadows%20creating%20a%20luxury%20aesthetic%20feel%20warm%20neutral%20tones%20professional%20studio%20shot%20clean%20composition&width=900&height=700&seq=hero-slide-1&orientation=landscape",
    alt: "高端美妝保養品展示",
  },
  {
    id: 2,
    image:
      "https://readdy.ai/api/search-image?query=A%20luxury%20fashion%20editorial%20photograph%20of%20designer%20handbags%20and%20premium%20electronics%20arranged%20elegantly%20on%20a%20soft%20neutral%20linen%20backdrop%20with%20warm%20golden%20hour%20lighting%20minimalist%20styling%20high-end%20product%20photography%20warm%20brown%20and%20beige%20color%20palette&width=900&height=700&seq=hero-slide-2&orientation=landscape",
    alt: "精品包袋與3C電子產品",
  },
  {
    id: 3,
    image:
      "https://readdy.ai/api/search-image?query=A%20premium%20home%20appliances%20lifestyle%20photograph%20of%20modern%20sleek%20kitchen%20gadgets%20and%20smart%20home%20devices%20arranged%20on%20a%20clean%20light%20wood%20surface%20with%20soft%20diffused%20lighting%20warm%20neutral%20tones%20contemporary%20minimalist%20interior%20styling%20professional%20product%20photography&width=900&height=700&seq=hero-slide-3&orientation=landscape",
    alt: "家電與生活好物",
  },
];

const SLIDE_INTERVAL = 4500;

export default function HeroSection() {
  const { isLoggedIn, loading: authLoading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [staySignedIn, setStaySignedIn] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
  }, []);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const handleLoginClick = () => {
    setAuthMode("login");
    setShowAuthModal(true);
  };

  const handleRegisterClick = () => {
    setAuthMode("register");
    setShowAuthModal(true);
  };

  return (
    <>
      <section className="relative w-full bg-stone-50">
        <div className="flex flex-col lg:flex-row min-h-[420px] md:min-h-[600px] lg:min-h-[680px]">
          {/* Left Side — Text & Action (Static) */}
          <div className="w-full lg:w-[45%] xl:w-[42%] flex flex-col justify-center px-6 md:px-12 lg:px-16 py-12 md:py-16 lg:py-0 bg-white z-10">
            <div className="max-w-md mx-auto lg:mx-0 w-full">
              {/* Tag */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 border border-rose-100 mb-6">
                <i className="ri-shield-star-line text-rose-600 text-xs" />
                <span className="font-body text-xs text-rose-700 font-medium">供應鏈認購平台</span>
              </div>

              {/* Heading */}
              <h1
                className="font-display text-3xl md:text-4xl lg:text-[42px] font-bold text-stone-900 leading-tight tracking-tight"
                style={{ fontFamily: 'Inter, "PingFang TC", "Microsoft JhengHei", sans-serif', letterSpacing: '0.05em', lineHeight: 1.4 }}
              >
                認購優質貨源，
                <br />
                輕鬆賺取差價
              </h1>

              {/* Sub-heading */}
              <p className="font-body text-sm md:text-base text-stone-500 mt-5 leading-relaxed">
                專業團隊選品，低價認購，高回報轉售。加入數萬名使用者的行列，享受穩定的利潤收益。
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-8 min-h-[48px]">
                {authLoading ? (
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <div className="h-12 rounded-full bg-stone-100 animate-pulse flex-1" />
                    <div className="h-12 rounded-full bg-stone-100 animate-pulse flex-1" />
                  </div>
                ) : isLoggedIn ? (
                  <>
                    <Link
                      to="/shop"
                      className="inline-flex items-center justify-center gap-2 font-body text-sm font-semibold px-7 py-3 rounded-full border-2 border-stone-800 text-stone-800 hover:bg-stone-800 hover:text-white transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-shopping-bag-line" />
                      進入認購中心
                    </Link>
                    <Link
                      to="/profile"
                      className="inline-flex items-center justify-center gap-2 font-body text-sm font-semibold px-7 py-3 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-user-line" />
                      前往個人中心
                    </Link>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleLoginClick}
                      className="inline-flex items-center justify-center gap-2 font-body text-sm font-semibold px-7 py-3 rounded-full border-2 border-stone-800 text-stone-800 hover:bg-stone-800 hover:text-white transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-login-circle-line" />
                      立即登入
                    </button>
                    <button
                      onClick={handleRegisterClick}
                      className="inline-flex items-center justify-center gap-2 font-body text-sm font-semibold px-7 py-3 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-user-add-line" />
                      免費註冊
                    </button>
                  </>
                )}
              </div>

              {/* Stay signed in checkbox */}
              {!authLoading && !isLoggedIn && (
                <label className="flex items-center gap-2 mt-5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={staySignedIn}
                    onChange={(e) => setStaySignedIn(e.target.checked)}
                    className="w-4 h-4 rounded border-stone-300 text-rose-800 accent-rose-800 focus:ring-2 focus:ring-rose-100"
                  />
                  <span className="font-body text-xs text-stone-400">保持登入狀態</span>
                </label>
              )}
            </div>
          </div>

          {/* Right Side — Image Carousel */}
          <div className="w-full lg:w-[55%] xl:w-[58%] relative overflow-hidden min-h-[320px] md:min-h-[420px] lg:min-h-0">
            {/* Slides */}
            {SLIDES.map((slide, index) => (
              <div
                key={slide.id}
                className="absolute inset-0 transition-opacity duration-700 ease-in-out"
                style={{ opacity: currentSlide === index ? 1 : 0, zIndex: currentSlide === index ? 1 : 0 }}
              >
                <img
                  src={slide.image}
                  alt={slide.alt}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
              </div>
            ))}

            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent z-[2]" />

            {/* Bottom Overlay Text */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 z-[3]">
              <div className="flex items-end justify-between">
                <div>
                  <p className="font-body text-xs text-white/80 font-medium mb-1">智慧選品 AI 驅動</p>
                  <p className="font-display text-lg md:text-xl font-bold text-white">
                    Subscribe in seconds with Smart Profit AI
                  </p>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="font-body text-xs text-white font-medium">即時更新中</span>
                </div>
              </div>
            </div>

            {/* Navigation Dots */}
            <div className="absolute bottom-20 md:bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-2 z-[4]">
              {SLIDES.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    currentSlide === index
                      ? "bg-white w-6"
                      : "bg-white/40 hover:bg-white/70"
                  }`}
                  aria-label={`前往第 ${index + 1} 張投影片`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode={authMode}
      />
    </>
  );
}